/**
 * GiveLeviProjectInformation — one visible, bilingual contractor entry
 * point on ProjectDetail that composes the existing supported paths
 * (image + PDF upload, Voice Notes, Vision video walkthrough) under
 * one banner and exposes the locked LPKI PR-2 lifecycle on uploaded
 * project files.
 *
 * Boundaries (from
 * /app/memory/LPKI_CONTRACTOR_UNIFIED_INTAKE_BOUNDED_PLAN_2026-08-05.md):
 *   - Zero new backend routes. Every network call targets an existing
 *     locked endpoint (POST /projects/{pid}/photos, VoiceNotes,
 *     VideoWalkRecorder, PR-2 lifecycle routes, PR-2 archive DELETE).
 *   - Zero new collections, zero MIME allowlist expansion.
 *   - Feature-flag gated (`REACT_APP_LEVI_LPKI_CONTRACTOR_INTAKE_ENABLED`).
 *     Default OFF. When the mount site sees the flag OFF, this
 *     component is not rendered at all (see ProjectDetail).
 *   - Backend lifecycle flag (`LEVI_LPKI_PROJECT_FILES_ENABLED`) is
 *     probed honestly on first attempt: a 404 with detail
 *     "endpoint_disabled" from any lifecycle route disables the
 *     approve/reject/defer/supersede/archive controls and surfaces a
 *     small bilingual banner so the contractor is never lied to.
 */
import { useEffect, useState, useRef } from "react";
import { api, explainSaveError } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import {
  Sparkles, Image as ImageIcon, FileText, Mic, Video,
  CheckCircle2, XCircle, Clock, Archive as ArchiveIcon,
  Replace, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";
import VoiceNotes from "@/components/VoiceNotes";
import VideoWalkRecorder from "@/components/videoWalk/VideoWalkRecorder";

// EN/ES tables kept local so we do not bloat onboarding_i18n.js for a
// single component. Same pattern used by other bounded slices.
const COPY = {
  en: {
    banner_title: "Give Levi Project Information",
    banner_sub: "Anyone can add info. Only you decide what becomes trusted project knowledge.",
    tile_photo: "Add photo or PDF",
    tile_photo_hint: "JPG, PNG, HEIC, WebP, or PDF",
    tile_voice: "Record voice note",
    tile_voice_hint: "Audio dictation to the project timeline",
    tile_video: "Start video walkthrough",
    tile_video_hint: "Vision-guided room walk",
    review_title: "Items awaiting your review",
    review_empty: "Nothing uploaded yet. Pick an input above to start.",
    source_contractor: "Contractor",
    source_customer: "Customer",
    state_unreviewed: "Unreviewed",
    state_approved: "Approved",
    state_rejected: "Rejected",
    state_deferred: "Deferred",
    state_superseded: "Superseded",
    category_label: "Category",
    action_approve: "Approve",
    action_reject: "Reject",
    action_defer: "Defer",
    action_supersede: "Supersede",
    action_archive: "Archive",
    action_correct_category: "Correct category",
    supersede_prompt: "Which newer file replaces this one?",
    supersede_cancel: "Cancel",
    supersede_confirm: "Supersede",
    lifecycle_disabled_title: "Lifecycle actions are currently disabled",
    lifecycle_disabled_body: "The project-file review workflow is paused for this environment. Uploads still succeed and are recorded as unreviewed.",
    unsupported_mime: "That file type is not supported. Allowed: JPG, PNG, HEIC/HEIF, WebP, PDF.",
    approved_uneditable: "Approved files cannot be archived. Supersede them with a new file to preserve project history.",
    superseded_uneditable: "Superseded files cannot be archived; the supersession record already preserves project history.",
    saved: "Saved.",
    archived: "Archived.",
    already_archived: "Already archived.",
    voice_section: "Voice notes",
    video_section: "Video walkthrough",
  },
  es: {
    banner_title: "Dale información del proyecto a Levi",
    banner_sub: "Cualquiera puede añadir información. Solo tú decides qué se convierte en conocimiento confiable del proyecto.",
    tile_photo: "Añadir foto o PDF",
    tile_photo_hint: "JPG, PNG, HEIC, WebP o PDF",
    tile_voice: "Grabar nota de voz",
    tile_voice_hint: "Dictado de audio al historial del proyecto",
    tile_video: "Iniciar recorrido de video",
    tile_video_hint: "Recorrido guiado por Vision",
    review_title: "Elementos pendientes de tu revisión",
    review_empty: "Aún no se ha subido nada. Elige una entrada arriba para empezar.",
    source_contractor: "Contratista",
    source_customer: "Cliente",
    state_unreviewed: "Sin revisar",
    state_approved: "Aprobado",
    state_rejected: "Rechazado",
    state_deferred: "Aplazado",
    state_superseded: "Sustituido",
    category_label: "Categoría",
    action_approve: "Aprobar",
    action_reject: "Rechazar",
    action_defer: "Aplazar",
    action_supersede: "Sustituir",
    action_archive: "Archivar",
    action_correct_category: "Corregir categoría",
    supersede_prompt: "¿Qué archivo más reciente reemplaza a éste?",
    supersede_cancel: "Cancelar",
    supersede_confirm: "Sustituir",
    lifecycle_disabled_title: "Las acciones del ciclo de vida están desactivadas",
    lifecycle_disabled_body: "El flujo de revisión de archivos del proyecto está pausado en este entorno. Las cargas siguen funcionando y quedan registradas como sin revisar.",
    unsupported_mime: "Ese tipo de archivo no está permitido. Permitidos: JPG, PNG, HEIC/HEIF, WebP, PDF.",
    approved_uneditable: "Los archivos aprobados no pueden archivarse. Sustitúyalos con un archivo nuevo para conservar el historial del proyecto.",
    superseded_uneditable: "Los archivos sustituidos no pueden archivarse; el registro de sustitución ya conserva el historial del proyecto.",
    saved: "Guardado.",
    archived: "Archivado.",
    already_archived: "Ya estaba archivado.",
    voice_section: "Notas de voz",
    video_section: "Recorrido de video",
  },
};

const CATEGORIES = [
  "photo", "plan", "insurance_estimate", "measurement_report",
  "supplier_quote", "prior_estimate", "permit", "inspection",
  "engineering", "specification", "general",
];

const ALLOWED_MIMES = new Set([
  "image/jpeg", "image/png", "image/heic", "image/heif",
  "image/webp", "application/pdf",
]);

const STATE_TONE = {
  unreviewed: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-50 text-rose-800 border-rose-200",
  deferred: "bg-sky-50 text-sky-800 border-sky-200",
  superseded: "bg-slate-100 text-slate-700 border-slate-300",
  archived: "bg-slate-100 text-slate-500 border-slate-300",
};

const isLifecycleDisabledDetail = (err) => {
  const d = err?.response?.data?.detail;
  return err?.response?.status === 404 && (d === "endpoint_disabled"
    || (d && typeof d === "object" && d.code === "endpoint_disabled"));
};

const GiveLeviProjectInformation = ({ projectId }) => {
  const { lang } = useLang();
  const c = COPY[lang === "es" ? "es" : "en"];
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [openSection, setOpenSection] = useState(null); // "voice" | "video" | null
  const [lifecycleDisabled, setLifecycleDisabled] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [supersedeFor, setSupersedeFor] = useState(null); // fid of predecessor being replaced

  const refresh = async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/projects/${projectId}/photos`);
      setFiles(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      // Non-fatal: keep the current list.
    }
  };

  useEffect(() => { refresh(); }, [projectId]);

  // ---------- Upload path (POST /projects/{pid}/photos) ---------------
  const handleFiles = async (fileList) => {
    if (!projectId) return;
    const list = Array.from(fileList || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const mime = (f.type || "").toLowerCase();
        if (!ALLOWED_MIMES.has(mime)) {
          toast.error(c.unsupported_mime);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(`/projects/${projectId}/photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          toast.error(explainSaveError(err, "upload"));
        }
      }
      await refresh();
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ---------- Lifecycle (locked PR-2 routes) --------------------------
  const runLifecycle = async (fid, verb, body = null) => {
    setBusyId(fid);
    try {
      const url = `/projects/${projectId}/files/${fid}/${verb}`;
      await (body ? api.post(url, body) : api.post(url));
      toast.success(c.saved);
      await refresh();
    } catch (err) {
      if (isLifecycleDisabledDetail(err)) {
        setLifecycleDisabled(true);
        toast.error(c.lifecycle_disabled_title);
      } else if (err?.response?.status === 400
        && err?.response?.data?.detail?.code === "archive_refused") {
        // Bilingual refusal already prepared by backend; prefer the
        // active-language string but fall back safely.
        const detail = err.response.data.detail;
        toast.error(lang === "es" ? detail.message_es : detail.message_en);
      } else {
        toast.error(explainSaveError(err, "action"));
      }
    } finally {
      setBusyId(null);
    }
  };

  const setCategory = async (fid, nextCategory) => {
    setBusyId(fid);
    try {
      await api.put(`/projects/${projectId}/files/${fid}/category`, {
        document_category: nextCategory,
      });
      toast.success(c.saved);
      await refresh();
    } catch (err) {
      if (isLifecycleDisabledDetail(err)) {
        setLifecycleDisabled(true);
        toast.error(c.lifecycle_disabled_title);
      } else {
        toast.error(explainSaveError(err, "category"));
      }
    } finally {
      setBusyId(null);
    }
  };

  const archive = async (fid) => {
    setBusyId(fid);
    try {
      const r = await api.delete(`/files/${fid}`);
      if (r?.data?.already_archived) toast(c.already_archived);
      else toast.success(c.archived);
      await refresh();
    } catch (err) {
      // Archive gate is FLAG-INDEPENDENT for approved/superseded.
      // Backend returns 400 with the bilingual detail we already
      // localize below.
      if (err?.response?.status === 400
        && err?.response?.data?.detail?.code === "archive_refused") {
        const detail = err.response.data.detail;
        toast.error(lang === "es" ? detail.message_es : detail.message_en);
      } else {
        toast.error(explainSaveError(err, "archive"));
      }
    } finally {
      setBusyId(null);
    }
  };

  const doSupersede = async (predecessorFid, successorFid) => {
    await runLifecycle(predecessorFid, "supersede", { superseded_by: successorFid });
    setSupersedeFor(null);
  };

  // ---------- Presentation helpers -----------------------------------
  const stateLabel = (s) => c[`state_${s || "unreviewed"}`] || (s || "");
  const supersedeCandidates = files.filter(
    (f) => f.id !== supersedeFor && (f.approval_state === "unreviewed" || f.approval_state === "approved"),
  );

  return (
    <section className="jc-card p-6" data-testid="give-levi-project-information">
      {/* Banner */}
      <div className="flex items-start gap-3">
        <div className="mt-1 p-2 rounded-full bg-[var(--jc-accent-soft,rgba(0,0,0,0.05))]">
          <Sparkles className="w-5 h-5" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-2xl" data-testid="glpi-banner-title">
            {c.banner_title}
          </h3>
          <p className="text-sm text-[var(--jc-text-soft)] mt-1">{c.banner_sub}</p>
        </div>
      </div>

      {lifecycleDisabled && (
        <div
          className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm"
          data-testid="glpi-lifecycle-disabled-banner"
          role="status"
        >
          <div className="font-medium">{c.lifecycle_disabled_title}</div>
          <div className="text-[var(--jc-text-soft)]">{c.lifecycle_disabled_body}</div>
        </div>
      )}

      {/* Three input tiles */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          type="button"
          className="jc-card p-4 text-left hover:border-[var(--jc-accent)] transition-colors"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="glpi-tile-photo"
        >
          <div className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" aria-hidden="true" />
            <FileText className="w-5 h-5" aria-hidden="true" />
          </div>
          <div className="mt-2 font-medium">{c.tile_photo}</div>
          <div className="text-xs text-[var(--jc-text-soft)] mt-1">{c.tile_photo_hint}</div>
          {uploading && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>{c.tile_photo}…</span>
            </div>
          )}
        </button>

        <button
          type="button"
          className="jc-card p-4 text-left hover:border-[var(--jc-accent)] transition-colors"
          onClick={() => setOpenSection((s) => (s === "voice" ? null : "voice"))}
          data-testid="glpi-tile-voice"
          aria-expanded={openSection === "voice"}
        >
          <Mic className="w-5 h-5" aria-hidden="true" />
          <div className="mt-2 font-medium flex items-center justify-between">
            <span>{c.tile_voice}</span>
            {openSection === "voice"
              ? <ChevronUp className="w-4 h-4" />
              : <ChevronDown className="w-4 h-4" />}
          </div>
          <div className="text-xs text-[var(--jc-text-soft)] mt-1">{c.tile_voice_hint}</div>
        </button>

        <button
          type="button"
          className="jc-card p-4 text-left hover:border-[var(--jc-accent)] transition-colors"
          onClick={() => setOpenSection((s) => (s === "video" ? null : "video"))}
          data-testid="glpi-tile-video"
          aria-expanded={openSection === "video"}
        >
          <Video className="w-5 h-5" aria-hidden="true" />
          <div className="mt-2 font-medium flex items-center justify-between">
            <span>{c.tile_video}</span>
            {openSection === "video"
              ? <ChevronUp className="w-4 h-4" />
              : <ChevronDown className="w-4 h-4" />}
          </div>
          <div className="text-xs text-[var(--jc-text-soft)] mt-1">{c.tile_video_hint}</div>
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.heic,.heif,.webp,.pdf,image/*,application/pdf"
        style={{ display: "none" }}
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="glpi-file-input"
      />

      {/* Voice / Video expansion sections — reuse existing components */}
      {openSection === "voice" && (
        <div className="mt-5" data-testid="glpi-voice-section">
          <div className="overline mb-2">{c.voice_section}</div>
          <VoiceNotes projectId={projectId} />
        </div>
      )}

      {openSection === "video" && (
        <div className="mt-5" data-testid="glpi-video-section">
          <div className="overline mb-2">{c.video_section}</div>
          <VideoWalkRecorder projectId={projectId} onFinalized={refresh} />
        </div>
      )}

      {/* Review list */}
      <div className="mt-6" data-testid="glpi-review-list">
        <div className="overline mb-2">{c.review_title}</div>
        {files.length === 0 ? (
          <div className="text-sm text-[var(--jc-text-soft)]" data-testid="glpi-review-empty">
            {c.review_empty}
          </div>
        ) : (
          <ul className="divide-y">
            {files.slice(0, 10).map((f) => {
              const state = f.approval_state || "unreviewed";
              const tone = STATE_TONE[state] || STATE_TONE.unreviewed;
              const uploader = f.uploader_role === "customer"
                ? c.source_customer : c.source_contractor;
              const rowBusy = busyId === f.id;
              return (
                <li key={f.id} className="py-3" data-testid={`glpi-row-${f.id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{f.original_filename || f.id}</div>
                      <div className="text-xs text-[var(--jc-text-soft)] mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${tone}`}
                              data-testid={`glpi-row-${f.id}-state`}>
                          {stateLabel(state)}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] bg-slate-50 border-slate-200 text-slate-700"
                              data-testid={`glpi-row-${f.id}-source`}>
                          {uploader}
                        </span>
                        <span>·</span>
                        <span>{c.category_label}:</span>
                        <select
                          className="text-xs border rounded px-1 py-0.5 bg-white"
                          value={f.document_category || "general"}
                          disabled={rowBusy || lifecycleDisabled || state === "superseded" || state === "archived"}
                          onChange={(e) => setCategory(f.id, e.target.value)}
                          data-testid={`glpi-row-${f.id}-category`}
                          aria-label={c.action_correct_category}
                        >
                          {CATEGORIES.map((k) => (
                            <option key={k} value={k}>{k}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {state === "unreviewed" && !lifecycleDisabled && (
                        <>
                          <button type="button" className="jc-btn jc-btn-xs" disabled={rowBusy}
                                  onClick={() => runLifecycle(f.id, "approve")}
                                  data-testid={`glpi-row-${f.id}-approve`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" />{c.action_approve}
                          </button>
                          <button type="button" className="jc-btn jc-btn-xs" disabled={rowBusy}
                                  onClick={() => runLifecycle(f.id, "reject")}
                                  data-testid={`glpi-row-${f.id}-reject`}>
                            <XCircle className="w-3 h-3 mr-1" />{c.action_reject}
                          </button>
                          <button type="button" className="jc-btn jc-btn-xs" disabled={rowBusy}
                                  onClick={() => runLifecycle(f.id, "defer")}
                                  data-testid={`glpi-row-${f.id}-defer`}>
                            <Clock className="w-3 h-3 mr-1" />{c.action_defer}
                          </button>
                        </>
                      )}
                      {(state === "unreviewed" || state === "approved") && !lifecycleDisabled && (
                        <button type="button" className="jc-btn jc-btn-xs" disabled={rowBusy}
                                onClick={() => setSupersedeFor(f.id)}
                                data-testid={`glpi-row-${f.id}-supersede`}>
                          <Replace className="w-3 h-3 mr-1" />{c.action_supersede}
                        </button>
                      )}
                      <button type="button" className="jc-btn jc-btn-xs" disabled={rowBusy}
                              onClick={() => archive(f.id)}
                              data-testid={`glpi-row-${f.id}-archive`}
                              title={state === "approved" ? c.approved_uneditable
                                     : state === "superseded" ? c.superseded_uneditable : undefined}>
                        <ArchiveIcon className="w-3 h-3 mr-1" />{c.action_archive}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Supersede picker (small inline dialog) */}
      {supersedeFor && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          data-testid="glpi-supersede-modal"
        >
          <div className="jc-card p-5 max-w-md w-full bg-white">
            <div className="font-medium">{c.supersede_prompt}</div>
            <ul className="mt-3 max-h-64 overflow-auto divide-y">
              {supersedeCandidates.length === 0 && (
                <li className="py-2 text-sm text-[var(--jc-text-soft)]">—</li>
              )}
              {supersedeCandidates.map((cand) => (
                <li key={cand.id}>
                  <button
                    type="button"
                    className="w-full text-left py-2 text-sm hover:bg-slate-50"
                    onClick={() => doSupersede(supersedeFor, cand.id)}
                    data-testid={`glpi-supersede-pick-${cand.id}`}
                  >
                    {cand.original_filename || cand.id}
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                className="jc-btn jc-btn-secondary"
                onClick={() => setSupersedeFor(null)}
                data-testid="glpi-supersede-cancel"
              >
                {c.supersede_cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GiveLeviProjectInformation;
